import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { api, fileUrl } from "../api";
import { useLang } from "../i18n";
import { Reveal, SectionHead } from "../components/Section";
import { GALLERY_PHOTOS } from "../data/site";

export default function Gallery() {
    const { t } = useLang();
    const [uploads, setUploads] = useState([]);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        api.get("/media").then((r) => setUploads(r.data.media || [])).catch(() => {});
    }, []);

    const photos = [
        ...GALLERY_PHOTOS.map((g) => ({ src: g.src, caption: g.caption, tag: g.tag })),
        ...uploads.map((m) => ({ src: fileUrl(m.path), caption: m.caption || "LAKOU moment", tag: m.category, video: (m.content_type || "").startsWith("video") })),
    ];

    return (
        <div data-testid="gallery-page" className="pt-32 pb-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHead eyebrow={t("home_gallery_eyebrow")} title={t("gallery_title")} testId="gallery-title" />
                <div className="mt-14 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
                    {photos.map((p, i) => (
                        <Reveal key={i} delay={(i % 6) * 0.05} className="mb-5 break-inside-avoid">
                            <button onClick={() => !p.video && setLightbox(p)} data-testid={`gallery-photo-${i}`}
                                className="group relative w-full rounded-2xl overflow-hidden border border-lakou-gold/15 hover:border-lakou-gold/40 transition-colors duration-500">
                                {p.video ? (
                                    <video src={p.src} controls playsInline preload="metadata" data-testid={`gallery-video-${i}`}
                                        className="w-full" aria-label={p.caption} />
                                ) : (
                                    <img src={p.src} alt={p.caption} loading="lazy" className="w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                )}
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-lakou-void/90 to-transparent p-5 pt-12 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                                    <div className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-lakou-gold">{p.tag}</div>
                                    <div className="text-sm text-lakou-cream mt-1">{p.caption}</div>
                                </div>
                            </button>
                        </Reveal>
                    ))}
                </div>
            </div>

            {lightbox && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="gallery-lightbox"
                    className="fixed inset-0 z-[80] bg-lakou-void/95 backdrop-blur-md flex items-center justify-center p-4"
                    onClick={() => setLightbox(null)}>
                    <button className="absolute top-6 right-6 p-2 text-lakou-grey hover:text-lakou-cream" aria-label="Close" data-testid="lightbox-close"><X size={26} /></button>
                    <motion.img initial={{ scale: 0.94 }} animate={{ scale: 1 }} src={lightbox.src} alt={lightbox.caption}
                        className="max-h-[85vh] max-w-full rounded-2xl border border-lakou-gold/20" />
                </motion.div>
            )}
        </div>
    );
}
